import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { GROUP_TAB_IDS, GroupAlertAccess, TabId } from 'core/utils/groupAlerts';
import { UserRole } from 'types';

import {
  G6_MATERIALS_CRUD,
  DEBTOR_REMINDERS,
  DIPLOMA_SEND_EMAIL,
  DIPLOMA_GENERATION,
  INVOICE_ISSUE,
} from 'core/featureFlags';

import {
  EDUCATION_ROLES,
  EDUCATION_SECTION_ROLES,
  FINANCE_PLAN_ROLES,
} from '../Palette/paletteRoles';
import {
  DEBTOR_CANDIDATES_ROLES,
  DIPLOMA_ERP_READ_ROLES,
  DIPLOMA_ERP_ROLES,
  EXPENSES_ACCESS_ROLES,
  FAKTUROWNIA_ACCESS_ROLES,
  GROUP_REVENUE_ROLES,
  GROUP_SKILLS_ROLES,
  GROUP_STAFF_FIELDS_ROLES,
  GROUP_STUDY_SUMMARY_ROLES,
  HOMEWORK_ERP_ROLES,
  MAILING_ACCESS_ROLES,
  MANAGEMENT_ROLES,
  MATERIALS_READ_ROLES,
  MATERIALS_WRITE_ROLES,
  META_GROUPS_ROLES,
  PANDADOC_ACCESS_ROLES,
  SURVEY_MANAGER_ROLES,
  TEACHER_INSIGHT_ROLES,
  ZOOM_ACCESS_ROLES,
  ZOOM_GROUP_TRIGGER_ROLES,
} from '../Palette/groupCardRoles';
import { SMS_ROLES } from '../Sms/useSmsPermissions';
import { AI_LESSON_ROLES } from '../AiAgents/useAiAgentPermissions';
import {
  AGREEMENT_CORRECT_ROLES,
  ENROLLMENT_UNDO_ROLES,
} from '../Students/agreementCorrectionModel';

/**
 * Capability booleans for the Group Dashboard, derived from the current user's
 * roles. Hiding a control is a UX affordance only — the backend remains the
 * authority on each mutation/export.
 *
 * 🔴 Флаг ЧТЕНИЯ — не косметика. Отказ бэкенда приходит ошибкой GraphQL без
 * кода, и блок, чей запрос отбит, рисовал её как «пусто»: «Записей нет»,
 * «0 PLN», «Проблем не найдено» — неправду о данных. Поэтому каждый флаг
 * `canRead*` / `canSee*Tab` — зеркало ОДНОГО списка бэкенда
 * (`Palette/groupCardRoles`), и им одновременно прячут блок и выключают
 * (`skip`) его запрос: запрос, которого роль не прочтёт, не уходит вовсе.
 */
export interface IGroupPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance`. */
  canExportFinance: boolean;
  /** Admin, AccountManager, SalesManager, FinanceManager. */
  canTerminate: boolean;
  /** (Admin, FinanceManager, AccountManager) AND the `DEBTOR_REMINDERS` flag. */
  canRemindDebtors: boolean;
  /**
   * BULK «Выставить фактуры» over the group — issue REAL Fakturownia VAT
   * documents for many students at once. (Admin, FinanceManager,
   * AccountManager) AND the `INVOICE_ISSUE` flag. Flag-gated for the same
   * reason as the single-student action: every created document gets a fiscal
   * number and CANNOT be deleted from the ERP, so it stays off on a
   * half-configured install. The backend is the authority
   * (`FAKTUROWNIA_ACCESS_ROLES`).
   */
  canIssueFaktura: boolean;
  /** (Admin, Methodist, Teacher) AND the `G6_MATERIALS_CRUD` flag. */
  canManageMaterials: boolean;
  /**
   * «Удалить» материал: `MATERIALS_WRITE_ROLES` (Admin, Methodist, Teacher).
   * Аккаунт-менеджер материалы читает, но удалить их сервер ему не даст.
   *
   * Флаг `G6_MATERIALS_CRUD` сюда НЕ входит намеренно: он с первого дня
   * (вкладка и флаг пришли одним коммитом) закрывает только создание и
   * правку, а удаление было открыто всегда — убирать его у Admin и
   * методиста этим флагом значило бы отнять существующую функцию.
   */
  canDeleteMaterials: boolean;
  /** Attach/replace/delete diplomas: `DIPLOMA_ERP_ROLES` — Admin, Methodist, AccountManager. */
  canManageDiplomas: boolean;
  /**
   * Скачать диплом (`diplomaDownloadUrl`): тоже `DIPLOMA_ERP_ROLES`.
   * Финансист список дипломов читает (`DIPLOMA_ERP_READ_ROLES`), а ссылку на
   * файл сервер ему не выдаёт — кнопки у него нет.
   */
  canDownloadDiplomas: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_SEND_EMAIL` flag. */
  canSendDiplomaEmails: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_GENERATION` flag. */
  canGenerateDiplomas: boolean;
  /**
   * ТЗ 0041 — видеть вкладку «Навыки» группы: Admin, AccountManager,
   * HRManager, Methodist (зеркало `CAREER_ROLES` на бэкенде).
   *
   * 🔴 Прятать вкладку обязательно, а не «желательно». Карточка группы
   * открыта шире карьерного домена, и роль без прав на `careerGroupSkills`
   * получит на клик не отказ, а пятисотку: маршрут и права на запрос — разные
   * оси, и на этом уже обжигались.
   */
  canSeeGroupSkills: boolean;
  /**
   * ТЗ 0075 — кнопка «Выгрузка для P&L» на вкладке «Финансы»: Admin,
   * FinanceManager (`FINANCE_PLAN_ROLES`, зеркало `PNL_EXPORT_ROLES` на
   * бэкенде). Уже, чем `canViewFinance`: аккаунт-менеджеру и продажам вкладка видна,
   * а заказ выгрузки сервер им не даст.
   */
  canExportPnl: boolean;
  /**
   * ERP-110 — «Исправить группу (ошибка ввода)» в меню строки ростера:
   * те же роли, что у перевода. Списки ролей — одни на приложение
   * (`agreementCorrectionModel`), общие с карточкой студента.
   */
  canCorrectAgreementGroup: boolean;
  /** ERP-110 — «Отменить зачисление»: Admin, FinanceManager, AccountManager. */
  canUndoEnrollment: boolean;
  /**
   * Кнопки шапки «Править», LMS, «В архив»: `EDUCATION_ROLES` (Admin,
   * AccountManager, SalesManager, HRManager) — те же роли, что у мутаций
   * группы на бэкенде и у «Править группу» в палитре.
   */
  canEditGroup: boolean;
  /**
   * «Добавить студента»: роли `appendStudentToGroup` на бэкенде — Admin,
   * AccountManager, SalesManager, FinanceManager (HR — нет).
   */
  canAppendStudent: boolean;
  /**
   * Меню синхронизации (Fakturownia / PandaDoc): роли их мутаций — Admin,
   * AccountManager, SalesManager, FinanceManager.
   */
  canSyncGroup: boolean;
  /**
   * Есть ли у роли список групп (`/education/groups`, маршрут `education`):
   * `EDUCATION_SECTION_ROLES`. Финансисту карточка группы открыта, а список —
   * нет, и «К списку групп» вело бы его на 404.
   */
  canOpenGroupList: boolean;

  /* ---------- чтение блоков (флаг = блок виден И запрос уходит) ---------- */

  /**
   * Поля группы `price`, `currency`, `teachers`, `deleteAvailable`
   * (`GROUP_STAFF_FIELDS_ROLES` = `MANAGEMENT_ROLES`): закрыты на бэкенде на
   * уровне ПОЛЯ. Без права операции списков групп/журналов и карточки
   * группы не запрашивают их (`$withStaffFields`), а UI прячет цену, валюту,
   * сумму договоров, преподавателей и признак «Архив».
   */
  canReadGroupStaffFields: boolean;
  /** Счётчики групп и журналов (`metaGroups`, `META_GROUPS_ROLES`): Admin, AM, Sales, HR. */
  canReadGroupCounts: boolean;

  /**
   * `groupRevenueAggregates`: Admin, FinanceManager, AccountManager,
   * SalesManager — денежные плитки KPI, «Деньги» обзора, сигнал «Должники».
   */
  canReadRevenue: boolean;
  /** `groupAttendanceSummary`: Admin, FinanceManager, AccountManager, Methodist. */
  canReadAttendanceSummary: boolean;
  /** `groupPerformanceSummary`: тот же список, что у посещаемости. */
  canReadPerformanceSummary: boolean;
  /**
   * Ростер группы (`GroupRoster`, `agreements_for`): `MANAGEMENT_ROLES`.
   * Методисту сервер отдаёт молча `[]` — тоже «пусто вместо отказа».
   */
  canReadRoster: boolean;
  /** Взносы договоров (`GroupFinanceContributions`): `MANAGEMENT_ROLES`. */
  canReadFinanceContributions: boolean;
  /** `debtorCandidates` (`PAYMENT_ACCESS_ROLES`): Admin, Fin, Sales, AM. */
  canReadDebtorCandidates: boolean;
  /** Фактуры и их агрегаты (`FAKTUROWNIA_ACCESS_ROLES`): Admin, Fin, Sales, AM. */
  canReadInvoices: boolean;
  /** Расходы (`EXPENSES_ACCESS_ROLES`): Admin, FinanceManager. */
  canReadExpenses: boolean;
  /** SendGrid-рассылки (`MAILING_ACCESS_ROLES`): Admin, AM, FinanceManager. */
  canReadMailings: boolean;
  /** SMS группе (`SMS_ROLES` = `MANAGEMENT_ROLES`). */
  canSendGroupSms: boolean;
  /** `surveyAnalytics` (`TEACHER_INSIGHT_ROLES`): менеджеры опросов + HR. */
  canReadSurveyAnalytics: boolean;
  /** «Обработать сейчас» (`ZOOM_GROUP_TRIGGER_ROLES`): Admin, Methodist. */
  canForceZoomProcess: boolean;

  /* ---------- вкладки (по главному запросу вкладки) ---------- */

  /** «Студенты» — ростер: `MANAGEMENT_ROLES`. */
  canSeeStudentsTab: boolean;
  /** «Zoom» — `allZoomRecordings` (`ZOOM_ACCESS_ROLES`): Admin, AM, Methodist. */
  canSeeZoomTab: boolean;
  /** «PandaDoc» — `pandadocDocuments` (`PANDADOC_ACCESS_ROLES`). */
  canSeePandadocTab: boolean;
  /** «Материалы» — `materials` (`MATERIALS_READ_ROLES`): Admin, Methodist, AM. */
  canSeeMaterialsTab: boolean;
  /** «ДЗ» — `groupHomeworkOverviewForErp` (`HOMEWORK_ERP_ROLES`). */
  canSeeHomeworkTab: boolean;
  /** «Опросы» — `groupSurveyAssignments` (`SURVEY_MANAGER_ROLES`); и «Прикрепить опрос». */
  canSeeSurveysTab: boolean;
  /**
   * ТЗ 0123 «Аналитика занятий» — `aiGroupLessonSummary` (`AI_LESSON_ROLES`
   * = бэкенд `LESSON_ANALYSIS_ROLES`): Admin, Methodist, AM, HR.
   */
  canSeeLessonAnalyticsTab: boolean;
  /** «Общение» — Slack и заметки: `MANAGEMENT_ROLES`. */
  canSeeCommunicationTab: boolean;
  /** «Рассылки» — если открыта хоть одна часть: SendGrid или SMS. */
  canSeeMailingsTab: boolean;
  /** «Задачи» — `staffTasks`: `MANAGEMENT_ROLES`. */
  canSeeTasksTab: boolean;
  /** «Финансы» — взносы договоров: `MANAGEMENT_ROLES`. */
  canSeeFinancesTab: boolean;
  /** «Дипломы» — `groupDiplomas` (`DIPLOMA_ERP_READ_ROLES`): Admin, AM, Methodist, Fin. */
  canSeeDiplomasTab: boolean;
}

/** Флаги видимости вкладок — из них `visibleGroupTabs` строит полосу. */
export type IGroupTabFlags = Pick<
  IGroupPermissions,
  | 'canSeeStudentsTab'
  | 'canSeeZoomTab'
  | 'canSeePandadocTab'
  | 'canSeeMaterialsTab'
  | 'canSeeGroupSkills'
  | 'canSeeSurveysTab'
  | 'canSeeLessonAnalyticsTab'
  | 'canSeeHomeworkTab'
  | 'canSeeCommunicationTab'
  | 'canSeeMailingsTab'
  | 'canSeeTasksTab'
  | 'canSeeFinancesTab'
  | 'canSeeDiplomasTab'
>;

/**
 * Вкладки карточки группы, доступные роли, в каноническом порядке
 * (`GROUP_TAB_IDS`). «Обзор» и «Расписание» видны всем, кому открыта
 * карточка (их запросы роль читает; виджеты внутри — по своим флагам).
 */
export const visibleGroupTabs = (flags: IGroupTabFlags): TabId[] => {
  const gate: Record<TabId, boolean> = {
    overview: true,
    students: flags.canSeeStudentsTab,
    schedule: true,
    lessonAnalytics: flags.canSeeLessonAnalyticsTab,
    zoom: flags.canSeeZoomTab,
    pandadoc: flags.canSeePandadocTab,
    materials: flags.canSeeMaterialsTab,
    skills: flags.canSeeGroupSkills,
    surveys: flags.canSeeSurveysTab,
    homework: flags.canSeeHomeworkTab,
    communication: flags.canSeeCommunicationTab,
    mailings: flags.canSeeMailingsTab,
    tasks: flags.canSeeTasksTab,
    finances: flags.canSeeFinancesTab,
    diplomas: flags.canSeeDiplomasTab,
  };
  return GROUP_TAB_IDS.filter((tabId) => gate[tabId]);
};

/**
 * Какие источники «Требует внимания» роль читает. Источник без права не
 * запрашивается и в итог не идёт; сигнал всегда ведёт на вкладку, которую
 * роль видит (должники — «Студенты», просрочка — «Финансы»: их списки шире
 * списков источников).
 */
export const groupAlertAccess = (
  permissions: Pick<
    IGroupPermissions,
    | 'canReadRevenue'
    | 'canReadAttendanceSummary'
    | 'canReadPerformanceSummary'
    | 'canReadFinanceContributions'
    | 'canSeeZoomTab'
    | 'canSeePandadocTab'
  >,
): GroupAlertAccess => ({
  debtors: permissions.canReadRevenue,
  attendanceBelow: permissions.canReadAttendanceSummary,
  noMarks: permissions.canReadPerformanceSummary,
  overdue: permissions.canReadFinanceContributions,
  zoomFailed: permissions.canSeeZoomTab,
  pandadocMissing: permissions.canSeePandadocTab,
});

const hasAnyRole = (roles: UserRole[], allowed: readonly UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Права карточки группы для набора ролей — чистая функция, чтобы матрицу
 * «роль × вкладка/блок» можно было проверить без React.
 */
export const groupPermissionsFor = (roles: UserRole[]): IGroupPermissions => {
  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canTerminate = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
    UserRole.FINANCIER,
  ]);

  const canRemindDebtors =
    DEBTOR_REMINDERS &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canIssueFaktura =
    INVOICE_ISSUE &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canManageMaterials =
    G6_MATERIALS_CRUD &&
    hasAnyRole(roles, [UserRole.ADMIN, UserRole.METHODIST, UserRole.TEACHER]);

  const canManageDiplomas = hasAnyRole(roles, DIPLOMA_ERP_ROLES);

  const isManagement = hasAnyRole(roles, MANAGEMENT_ROLES);
  const canReadMailings = hasAnyRole(roles, MAILING_ACCESS_ROLES);
  const canSendGroupSms = hasAnyRole(roles, SMS_ROLES);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canTerminate,
    canRemindDebtors,
    canIssueFaktura,
    canManageMaterials,
    canDeleteMaterials: hasAnyRole(roles, MATERIALS_WRITE_ROLES),
    canManageDiplomas,
    canDownloadDiplomas: canManageDiplomas,
    canSendDiplomaEmails: DIPLOMA_SEND_EMAIL && canManageDiplomas,
    canGenerateDiplomas: DIPLOMA_GENERATION && canManageDiplomas,
    // Список берём ОДИН на приложение (`CAREER_ROLES`), а не переписываем
    // рядом: две копии одного правила расходятся молча.
    canSeeGroupSkills: hasAnyRole(roles, GROUP_SKILLS_ROLES),
    canExportPnl: hasAnyRole(roles, FINANCE_PLAN_ROLES),
    canCorrectAgreementGroup: hasAnyRole(roles, AGREEMENT_CORRECT_ROLES),
    canUndoEnrollment: hasAnyRole(roles, ENROLLMENT_UNDO_ROLES),
    canEditGroup: hasAnyRole(roles, EDUCATION_ROLES),
    // Тот же список, что у расторжения: Admin, AM, Sales, Finance.
    canAppendStudent: canTerminate,
    canSyncGroup: canTerminate,
    canOpenGroupList: hasAnyRole(roles, EDUCATION_SECTION_ROLES),

    canReadGroupStaffFields: hasAnyRole(roles, GROUP_STAFF_FIELDS_ROLES),
    canReadGroupCounts: hasAnyRole(roles, META_GROUPS_ROLES),
    canReadRevenue: hasAnyRole(roles, GROUP_REVENUE_ROLES),
    canReadAttendanceSummary: hasAnyRole(roles, GROUP_STUDY_SUMMARY_ROLES),
    canReadPerformanceSummary: hasAnyRole(roles, GROUP_STUDY_SUMMARY_ROLES),
    canReadRoster: isManagement,
    canReadFinanceContributions: isManagement,
    canReadDebtorCandidates: hasAnyRole(roles, DEBTOR_CANDIDATES_ROLES),
    canReadInvoices: hasAnyRole(roles, FAKTUROWNIA_ACCESS_ROLES),
    canReadExpenses: hasAnyRole(roles, EXPENSES_ACCESS_ROLES),
    canReadMailings,
    canSendGroupSms,
    canReadSurveyAnalytics: hasAnyRole(roles, TEACHER_INSIGHT_ROLES),
    canForceZoomProcess: hasAnyRole(roles, ZOOM_GROUP_TRIGGER_ROLES),

    canSeeStudentsTab: isManagement,
    canSeeZoomTab: hasAnyRole(roles, ZOOM_ACCESS_ROLES),
    canSeePandadocTab: hasAnyRole(roles, PANDADOC_ACCESS_ROLES),
    canSeeMaterialsTab: hasAnyRole(roles, MATERIALS_READ_ROLES),
    canSeeHomeworkTab: hasAnyRole(roles, HOMEWORK_ERP_ROLES),
    canSeeSurveysTab: hasAnyRole(roles, SURVEY_MANAGER_ROLES),
    canSeeLessonAnalyticsTab: hasAnyRole(roles, AI_LESSON_ROLES),
    canSeeCommunicationTab: isManagement,
    canSeeMailingsTab: canReadMailings || canSendGroupSms,
    canSeeTasksTab: isManagement,
    canSeeFinancesTab: isManagement,
    canSeeDiplomasTab: hasAnyRole(roles, DIPLOMA_ERP_READ_ROLES),
  };
};

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans,
 * mirroring the backend permission lists (`Palette/groupCardRoles`).
 */
export const useGroupPermissions = (): IGroupPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return groupPermissionsFor(roles);
};
